import React, { useEffect, useState } from 'react';
import { db, collection, onSnapshot, addDoc, updateDoc, doc, deleteDoc, Timestamp } from '../firebase';
import { useAuth } from '../App';
import { Plus, Trash2, Edit2, X, ClipboardList, Loader2 } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { toast } from 'sonner';

export interface ServiceType {
  id: string;
  name: string;
  description?: string;
  createdAt: Timestamp;
}

const ServiceTypesPage: React.FC = () => {
  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingType, setEditingType] = useState<ServiceType | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');

  const { isAdmin, isManager } = useAuth();
  const hasAccess = isAdmin || isManager;

  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, 'service_types'),
      async (snapshot) => {
        const types: ServiceType[] = [];
        snapshot.forEach((doc) => {
          types.push({ id: doc.id, ...doc.data() } as ServiceType);
        });

        // If empty, seed default values
        if (types.length === 0 && hasAccess) {
          try {
            const defaults = [
              { name: 'Service', description: 'Preventative and routine maintenance services' },
              { name: 'Repair', description: 'Corrective repairs and troubleshooting' },
              { name: 'Supply and Installation', description: 'Procurement, supply, and integration of new equipment' }
            ];
            for (const d of defaults) {
              await addDoc(collection(db, 'service_types'), {
                name: d.name,
                description: d.description,
                createdAt: Timestamp.now()
              });
            }
          } catch (e) {
            console.error('Error seeding default service types:', e);
          }
        } else {
          setServiceTypes(types.sort((a, b) => b.createdAt.seconds - a.createdAt.seconds));
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error loading service types:', error);
        toast.error('Failed to load service types');
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [hasAccess]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error('Service type name is required');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingType) {
        await updateDoc(doc(db, 'service_types', editingType.id), {
          name: name.trim(),
          description: description.trim()
        });
        toast.success('Service type updated successfully');
      } else {
        await addDoc(collection(db, 'service_types'), {
          name: name.trim(),
          description: description.trim(),
          createdAt: Timestamp.now()
        });
        toast.success('Service type registered successfully');
      }
      closeModal();
    } catch (error) {
      console.error('Error saving service type:', error);
      toast.error('Failed to save service type');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string, typeName: string) => {
    if (!window.confirm(`Are you sure you want to delete the service type "${typeName}"?`)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'service_types', id));
      toast.success('Service type deleted successfully');
    } catch (error) {
      console.error('Error deleting service type:', error);
      toast.error('Failed to delete service type');
    }
  };

  const openModal = (type: ServiceType | null = null) => {
    if (type) {
      setEditingType(type);
      setName(type.name);
      setDescription(type.description || '');
    } else {
      setEditingType(null);
      setName('');
      setDescription('');
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingType(null);
    setName('');
    setDescription('');
  };

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-6 text-center">
        <h3 className="text-xl font-black text-red-600 mb-2">Access Denied</h3>
        <p className="text-slate-500 font-medium">You do not have permission to manage service types.</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" id="service-types-module">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black text-slate-900 tracking-tight">Service Types</h1>
          <p className="text-slate-500 font-medium text-sm mt-1">Register and manage service offerings used across clients and reports.</p>
        </div>
        <button
          onClick={() => openModal(null)}
          className="flex items-center justify-center gap-2 px-5 py-3.5 bg-orange-500 hover:bg-orange-600 text-white rounded-2xl font-bold shadow-lg shadow-orange-500/25 transition-all active:scale-95 cursor-pointer"
        >
          <Plus className="w-5 h-5" />
          <span>Register Service Type</span>
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>
      ) : serviceTypes.length === 0 ? (
        <div className="bg-white rounded-[2.5rem] border border-slate-100 p-12 text-center shadow-sm">
          <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <ClipboardList className="w-8 h-8" />
          </div>
          <h3 className="text-lg font-black text-slate-900 mb-1">No Service Types Registered</h3>
          <p className="text-slate-500 font-medium max-w-sm mx-auto mb-6">Register service types to define the exact services Bright Technical Systems provides to clients.</p>
          <button
            onClick={() => openModal(null)}
            className="px-5 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all cursor-pointer"
          >
            Add Your First Service Type
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {serviceTypes.map((type) => (
            <motion.div
              key={type.id}
              layout
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all flex flex-col justify-between"
            >
              <div>
                <div className="w-10 h-10 rounded-xl bg-orange-50 flex items-center justify-center mb-4">
                  <ClipboardList className="w-5 h-5 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2 truncate">{type.name}</h3>
                <p className="text-slate-500 text-sm font-medium line-clamp-3 mb-6 leading-relaxed">
                  {type.description || 'No description provided.'}
                </p>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-slate-50">
                <button
                  onClick={() => openModal(type)}
                  className="p-2.5 text-slate-400 hover:text-blue-900 hover:bg-blue-50 rounded-xl transition-all"
                  title="Edit service type"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(type.id, type.name)}
                  className="p-2.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-xl transition-all"
                  title="Delete service type"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Register/Edit Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeModal}
              className="absolute inset-0 bg-blue-950/40 backdrop-blur-sm"
            />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-[2.5rem] w-full max-w-md p-8 relative z-10 shadow-2xl overflow-hidden"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-xl font-black text-slate-900">
                    {editingType ? 'Edit Service Type' : 'Register Service Type'}
                  </h3>
                  <p className="text-slate-500 font-medium text-xs mt-0.5">
                    {editingType ? 'Update service type specifications.' : 'Define a new type of client service.'}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="p-2.5 hover:bg-slate-100 text-slate-400 hover:text-slate-600 rounded-xl transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Service Type Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="e.g. Mechanical Vent Service"
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold placeholder-slate-400"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Description
                  </label>
                  <textarea
                    rows={4}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    placeholder="Describe what this service covers..."
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-100 rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-800/10 transition-all font-bold placeholder-slate-400 resize-none"
                  />
                </div>

                <div className="flex gap-3 pt-4 border-t border-slate-50">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="flex-1 px-4 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 rounded-2xl font-bold transition-all cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex-1 px-4 py-3 bg-blue-900 hover:bg-blue-800 text-white rounded-2xl font-bold transition-all disabled:opacity-50 cursor-pointer flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <span>{editingType ? 'Save Changes' : 'Register Type'}</span>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ServiceTypesPage;
